import { useMemo, useState } from "react";
import { ChevronIcon, SearchIcon, StarIcon } from "./Icons";
import type {
  SourceLibraryFilters,
  SourceLibraryItem,
  SourceLibraryTab,
} from "./types";
import "./review.css";

const FILTER_OPTIONS = {
  practiceArea: [
    "All areas",
    "Corporate / M&A",
    "Commercial",
    "Litigation",
    "Regulatory",
    "Employment",
  ],
  geography: ["All", "European Union", "United Kingdom", "United States"],
  ownership: ["Project", "Created by me", "Starred only"],
};

export interface SourcesLibraryProps {
  items: SourceLibraryItem[];
  initialTab?: SourceLibraryTab;
  initialFilters?: Partial<SourceLibraryFilters>;
  onFiltersChange?: (filters: SourceLibraryFilters) => void;
  onStarItem?: (item: SourceLibraryItem, starred: boolean) => void | Promise<void>;
  onOpenItem?: (item: SourceLibraryItem) => void;
}

export function SourcesLibrary({
  items,
  initialTab = "workflows",
  initialFilters,
  onFiltersChange,
  onStarItem,
  onOpenItem,
}: SourcesLibraryProps) {
  const [tab, setTab] = useState<SourceLibraryTab>(initialTab);
  const [filters, setFilters] = useState<SourceLibraryFilters>({
    practiceArea: "All areas",
    geography: "All",
    ownership: "Project",
    query: "",
    ...initialFilters,
  });

  function patchFilters(patch: Partial<SourceLibraryFilters>) {
    const next = { ...filters, ...patch };
    setFilters(next);
    onFiltersChange?.(next);
  }

  const visibleItems = useMemo(() => {
    const query = filters.query.trim().toLocaleLowerCase();
    return items.filter((item) => {
      if (item.tab !== tab) return false;
      if (filters.ownership === "Starred only" && !item.starred) return false;
      if (!query) return true;
      return `${item.kicker} ${item.title} ${item.description} ${item.meta}`
        .toLocaleLowerCase()
        .includes(query);
    });
  }, [filters.ownership, filters.query, items, tab]);

  return (
    <section className="di-review di-library" aria-label="Sources library">
      <aside className="di-library__filters" aria-label="Source filters">
        <FilterGroup
          label="Practice area"
          name="source-practice-area"
          onChange={(practiceArea) => patchFilters({ practiceArea })}
          options={FILTER_OPTIONS.practiceArea}
          value={filters.practiceArea}
        />
        <FilterGroup
          label="Geography"
          name="source-geography"
          onChange={(geography) => patchFilters({ geography })}
          options={FILTER_OPTIONS.geography}
          value={filters.geography}
        />
        <FilterGroup
          label="Ownership"
          name="source-ownership"
          onChange={(ownership) => patchFilters({ ownership })}
          options={FILTER_OPTIONS.ownership}
          value={filters.ownership}
        />
      </aside>

      <div className="di-library__main">
        <div className="di-library__tabs">
          <div role="tablist" aria-label="Source type">
            {([
              ["prompts", "Prompts"],
              ["workflows", "Workflows"],
              ["playbooks", "Playbooks"],
            ] as Array<[SourceLibraryTab, string]>).map(([value, label]) => (
              <button
                aria-selected={tab === value}
                key={value}
                onClick={() => setTab(value)}
                role="tab"
                type="button"
              >
                {label}
              </button>
            ))}
          </div>
          <label className="di-search">
            <SearchIcon />
            <span className="di-visually-hidden">Search sources</span>
            <input
              onChange={(event) => patchFilters({ query: event.target.value })}
              placeholder="Search sources"
              type="search"
              value={filters.query}
            />
          </label>
        </div>

        <div className="di-library__scroll">
          {visibleItems.length > 0 ? (
            <div className="di-library-grid">
              {visibleItems.map((item) => (
                <article className="di-library-card" key={item.id}>
                  <header>
                    <span className="di-kicker">{item.kicker}</span>
                    <button
                      aria-label={`${item.starred ? "Unstar" : "Star"} ${item.title}`}
                      className="di-icon-button di-icon-button--star"
                      disabled={!onStarItem}
                      onClick={() => void onStarItem?.(item, !item.starred)}
                      title={!onStarItem ? "Favourites are not connected." : undefined}
                      type="button"
                    >
                      <StarIcon filled={item.starred} />
                    </button>
                  </header>
                  <h2>{item.title}</h2>
                  <p>{item.description}</p>
                  <footer>
                    <span>{item.meta}</span>
                    <button
                      className="di-btn di-btn--ghost"
                      disabled={!onOpenItem}
                      onClick={() => onOpenItem?.(item)}
                      title={!onOpenItem ? "Opening library items is not connected." : undefined}
                      type="button"
                    >
                      {item.tab === "prompts"
                        ? "Use prompt"
                        : item.tab === "workflows"
                          ? "Run workflow"
                          : "Open playbook"}
                      <ChevronIcon />
                    </button>
                  </footer>
                </article>
              ))}
            </div>
          ) : (
            <div className="di-library-empty">
              <h2>No matching sources</h2>
              <p>Change the filters or search terms to widen this view.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function FilterGroup({
  label,
  name,
  options,
  value,
  onChange,
}: {
  label: string;
  name: string;
  options: string[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <fieldset className="di-filter-group">
      <legend>{label}</legend>
      {options.map((option) => (
        <label className="di-radio" key={option}>
          <input
            checked={value === option}
            name={name}
            onChange={() => onChange(option)}
            type="radio"
          />
          <span aria-hidden="true" />
          {option}
        </label>
      ))}
    </fieldset>
  );
}
